/**
 * عيادتي — clinics and their calendar, on one screen.
 *
 * The legacy build kept these together and the rebuild had split them into two
 * sidebar entries, "إدارة المواعيد" and "العيادات". That split does not match
 * how the work is done: a doctor sets a working window and immediately wants to
 * see what it did to the month, and the calendar colours its days *by clinic* —
 * so the legend for the calendar was on a different page from the calendar.
 *
 * Top to bottom, matching the legacy order: add a clinic, the clinic cards, the
 * card carrying its whole configuration, then the month calendar over
 * every clinic. The الروشتة designer opens from the header, because a
 * letterhead is part of how a clinic presents itself.
 *
 * Covers working window, breaks, prices, deposits, extra services and the
 * service-group link that mirrors a price list across clinics.
 *
 * Replaces `renderClinicsManagement`, `openClinicModal`, `editClinic`,
 * `handleDoctorSaveClinic`, `addBreakRow`, `addClinicServiceRow`,
 * `populateClinicSyncOptions`, `copyAllServicesFromClinic`, `toggleClinicSync`,
 * `syncServicesBetweenClinics` and `showClinicServicesModal`.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { clinicApi } from '../../api/endpoints';
import { validateClinic } from '../../domain/validation';
import { generateSlots } from '../../domain/slots';
import { ARABIC_DAYS, DAY_KEYS, formatTime12 } from '../../domain/dates';
import { formatCurrency } from '../../domain/pricing';
import { resolveClinicRgb } from './calendar/legacyTheme';
import { DoctorSchedule } from './DoctorSchedule';
import { PrescriptionDesignerModal } from './PrescriptionDesignerModal';

/** Palette offered by the clinic editor. The backend rejects a duplicate colour
 *  per doctor, which is how the calendar keeps clinics visually distinct — so a
 *  doctor with more than eight clinics used to run out of choices entirely. */
const CLINIC_COLORS = [
  '#06b6d4', '#a855f7', '#10b981', '#f59e0b',
  '#ef4444', '#3b82f6', '#ec4899', '#14b8a6',
  '#8b5cf6', '#f97316', '#22c55e', '#0ea5e9',
  '#e11d48', '#eab308', '#6366f1', '#84cc16',
];

/** Working-day patterns that cover almost every Egyptian clinic. */
const DAY_PRESETS = [
  {
    label: 'الأحد — الخميس',
    days: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
  },
  {
    label: 'السبت — الأربعاء',
    days: ['saturday', 'sunday', 'monday', 'tuesday', 'wednesday'],
  },
  { label: 'أيام فردية', days: ['sunday', 'tuesday', 'thursday'] },
  { label: 'أيام زوجية', days: ['saturday', 'monday', 'wednesday'] },
  { label: 'كل الأيام', days: [...DAY_KEYS] },
];

/** Consultation lengths worth one click rather than a typed number. */
const DURATION_PRESETS = [10, 15, 20, 30, 45, 60];

/**
 * A hue per label on the clinic card.
 *
 * The clinic colour identifies the card; repeating it for every glyph inside
 * flattens the card back into a single block. Giving each label its own hue is
 * how the legacy cards stayed readable at a glance — money green, time amber —
 * and it means the eye can find "مدة الكشف" without reading the four labels.
 *
 * `delay` staggers the breathing animation. Without it every icon pulses
 * together and the card reads as flickering rather than alive.
 */
const LABEL_ACCENTS = {
  exam: { icon: 'stethoscope', color: '#34d399', delay: '0s' },
  followup: { icon: 'repeat', color: '#22d3ee', delay: '0.45s' },
  hours: { icon: 'clock', color: '#fbbf24', delay: '0.9s' },
  duration: { icon: 'hourglass', color: '#c084fc', delay: '1.35s' },
  services: { icon: 'sparkles', color: '#fb7185', delay: '1.8s' },
  days: { icon: 'calendar-days', color: '#60a5fa', delay: '2.25s' },
  phone: { icon: 'phone', color: '#22d3ee', delay: '2.7s' },
  map: { icon: 'map', color: '#34d399', delay: '3.15s' },
};

const emptyClinic = () => ({
  name: '',
  address: '',
  mobile: '',
  landline: '',
  mapUrl: '',
  color: CLINIC_COLORS[0],
  workingDays: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'],
  startTime: '10:00',
  endTime: '18:00',
  duration: 30,
  breaks: [],
  maxBookingsPerSlot: 1,
  examination_price: 300,
  followup_price: 150,
  requireDeposit: false,
  examination_deposit_percentage: 30,
  examination_deposit_amount: '',
  followup_deposit_percentage: 30,
  followup_deposit_amount: '',
  instapay_account: '',
  extra_services: [],
});

export function DoctorClinics() {
  const { doctorId } = useAuth();
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => clinicApi.listByDoctor(doctorId), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId),
  });
  // Memoised because the preview effect below depends on it — a fresh [] each
  // render would re-run that effect on every render.
  const clinics = useMemo(() => state.data || [], [state.data]);

  const [editing, setEditing] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [adding, setAdding] = useState(false);
  const [designerOpen, setDesignerOpen] = useState(false);


  const handleSave = async (clinic) => {
    try {
      if (clinic.id) {
        await clinicApi.update(clinic.id, clinic);
        success('تم تحديث العيادة');
      } else {
        await clinicApi.create({ ...clinic, doctor_id: doctorId });
        success('تمت إضافة العيادة');
      }
      setEditing(null);
      setAdding(false);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ العيادة');
    }
  };

  const handleLink = async (sourceId) => {
    if (!linkTarget) return;
    try {
      await clinicApi.linkServices(linkTarget.id, sourceId);
      success('تم ربط قائمة الخدمات بين العيادتين');
      setLinkTarget(null);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر ربط الخدمات');
    }
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    try {
      await clinicApi.unlinkServices(unlinkTarget.id);
      success('تم فك ارتباط الخدمات');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر فك الارتباط');
    } finally {
      setUnlinkTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="العيادات"
        subtitle={`${clinics.length} عيادة — المواعيد والتصميم في مكان واحد`}
        icon="building-2"
        actions={
          <>
            {/* The legacy clinics screen led with this button. The letterhead
                belongs with the clinics, not buried in account settings. */}
            <Button variant="ghost" icon="file-text" onClick={() => setDesignerOpen(true)}>
              الروشتة
            </Button>
            <Button
              icon={adding ? 'x' : 'plus'}
              variant={adding ? 'ghost' : 'primary'}
              onClick={() => setAdding((open) => !open)}
            >
              {adding ? 'إغلاق' : 'إضافة عيادة'}
            </Button>
          </>
        }
      />

      {/* The add form opens in place, above the list. Adding a clinic is the
          one thing this page exists for, and burying it in a modal meant the
          list it belongs to disappeared behind an overlay while you filled
          it in. */}
      {adding && (
        <InlineAddClinic
          onCancel={() => setAdding(false)}
          onSave={handleSave}
          onNeedFullEditor={(draft) => {
            setAdding(false);
            setEditing(draft);
          }}
        />
      )}

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && clinics.length === 0 ? (
        <Spinner label="جاري تحميل العيادات..." />
      ) : clinics.length === 0 ? (
        <Panel>
          <EmptyState
            icon="building"
            title="لا توجد عيادات"
            message="أضف عيادتك الأولى لتبدأ في استقبال الحجوزات — ثم سيظهر تقويم مواعيدها بالأسفل."
            action={
              <Button icon="plus" onClick={() => setAdding(true)}>
                إضافة عيادة
              </Button>
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {clinics.map((clinic) => (
            <ClinicCard
              key={clinic.id}
              clinic={clinic}
              onEdit={() => setEditing({ ...emptyClinic(), ...clinic })}
              onLink={() => setLinkTarget(clinic)}
              onUnlink={() => setUnlinkTarget(clinic)}
            />
          ))}
        </div>
      )}

      {/* The month calendar over every clinic — the second half of the legacy
          "العيادات" screen. It reads the clinics list this page already holds,
          so a working-window edit above is reflected below without a reload. */}
      <DoctorSchedule embedded clinics={clinics} clinicsLoading={state.loading} />

      <PrescriptionDesignerModal open={designerOpen} onClose={() => setDesignerOpen(false)} />

      <ClinicFormModal clinic={editing} onClose={() => setEditing(null)} onSave={handleSave} />

      <LinkServicesModal
        clinic={linkTarget}
        clinics={clinics}
        onClose={() => setLinkTarget(null)}
        onLink={handleLink}
      />

      <ConfirmDialog
        open={Boolean(unlinkTarget)}
        onClose={() => setUnlinkTarget(null)}
        onConfirm={handleUnlink}
        title="فك ارتباط الخدمات"
        confirmLabel="فك الارتباط"
        message={`ستحتفظ ${unlinkTarget?.name || 'العيادة'} بقائمة خدماتها الحالية، لكن أي تعديل لاحق لن ينعكس على العيادات الأخرى.`}
      />
    </div>
  );
}

/* ========================================================================== */

/**
 * One clinic, in that clinic's own colour.
 *
 * The colour is not decoration. The backend refuses two clinics the same one
 * per doctor precisely so they can be told apart, and the calendar below tints
 * its days by it — so the card is the legend for the calendar. Rendering all of
 * them in the same grey made that legend useless and the list hard to scan.
 *
 * Everything visual derives from `--clinic-rgb`; see .clinic-card in the
 * stylesheet. The card carries the whole configuration, which is why the
 * separate "as the patient sees it" preview panel below it was redundant and
 * has gone.
 */
function ClinicCard({ clinic, onEdit, onLink, onUnlink }) {
  const slots = useMemo(
    () =>
      clinic.startTime && clinic.endTime && clinic.duration
        ? generateSlots(clinic.startTime, clinic.endTime, clinic.duration, clinic.breaks)
        : clinic.slots || [],
    [clinic],
  );

  const rgb = useMemo(() => resolveClinicRgb(clinic.color).join(", "), [clinic.color]);

  const workingDays = Array.isArray(clinic.workingDays) ? clinic.workingDays : [];
  const services = clinic.extra_services || [];
  const servicesTotal = services.reduce(
    (sum, service) => sum + (Number(service.service_price) || 0),
    0,
  );

  return (
    <Panel
      padded={false}
      className="clinic-card rounded-2xl"
      style={{ '--clinic-rgb': rgb }}
    >
      <div className="clinic-card__bar" aria-hidden="true" />

      <div className="space-y-4 p-5">
        {/* Identity */}
        <div className="flex items-start gap-3">
          <span className="clinic-card__icon grid h-11 w-11 shrink-0 place-items-center rounded-xl">
            <Icon name="map-pin" size={20} />
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-bold text-white">{clinic.name}</h3>
            {clinic.address && (
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-400">
                <Icon name="navigation" size={11} className="shrink-0" />
                <span className="truncate">{clinic.address}</span>
              </p>
            )}

            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge
                className="border-transparent"
                icon="calendar-check"
              >
                <span style={{ color: `rgb(${rgb})` }}>{slots.length} موعد يومياً</span>
              </Badge>
              {clinic.requireDeposit && (
                <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="wallet">
                  يتطلب عربون
                </Badge>
              )}
              {clinic.service_group_id && (
                <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-300" icon="link">
                  خدمات مرتبطة
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* The four figures that decide whether a patient books */}
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <Cell
            accent={LABEL_ACCENTS.exam}
            label="الكشف"
            value={formatCurrency(clinic.examination_price)}
          />
          <Cell
            accent={LABEL_ACCENTS.followup}
            label="المتابعة"
            value={formatCurrency(clinic.followup_price)}
          />
          <Cell
            accent={LABEL_ACCENTS.hours}
            label="ساعات العمل"
            value={
              clinic.startTime && clinic.endTime
                ? `${formatTime12(clinic.startTime)} — ${formatTime12(clinic.endTime)}`
                : '—'
            }
          />
          <Cell
            accent={LABEL_ACCENTS.duration}
            label="مدة الكشف"
            value={clinic.duration ? `${clinic.duration} دقيقة` : '—'}
          />
        </dl>

        {/* Working days — the row that answers "is it open then" fastest */}
        <div className="flex flex-wrap items-center gap-1.5">
          <LabelIcon accent={LABEL_ACCENTS.days} size={13} className="ml-0.5" />
          {DAY_KEYS.map((day) => {
            const open = workingDays.some((d) => String(d).toLowerCase() === day);
            return (
              <span
                key={day}
                className={`rounded-lg px-2 py-1 text-[10px] font-bold transition-colors ${
                  open ? 'clinic-card__day-on' : 'bg-slate-900/80 text-slate-600'
                }`}
              >
                {ARABIC_DAYS[day]}
              </span>
            );
          })}
        </div>

        {services.length > 0 && (
          <div className="clinic-card__services rounded-xl p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-[11px] font-bold text-slate-200">
                <LabelIcon accent={LABEL_ACCENTS.services} size={12} />
                خدمات إضافية
              </p>
              <span className="text-[11px] font-bold" style={{ color: `rgb(${rgb})` }}>
                {services.length} خدمة · {formatCurrency(servicesTotal)}
              </span>
            </div>
            <ul className="space-y-1">
              {services.map((service, index) => (
                <li
                  key={service.id ?? index}
                  className="flex justify-between gap-2 text-[11px] text-slate-400"
                >
                  <span className="truncate">{service.service_name_ar}</span>
                  <span className="shrink-0 font-bold text-slate-200">
                    {formatCurrency(service.service_price)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(clinic.mobile || clinic.mapUrl) && (
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
            {clinic.mobile && (
              <span className="flex items-center gap-1.5" dir="ltr">
                <LabelIcon accent={LABEL_ACCENTS.phone} size={12} />
                {clinic.mobile}
              </span>
            )}
            {clinic.mapUrl && (
              <a
                href={clinic.mapUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-1.5 font-bold transition-opacity hover:opacity-75"
                style={{ color: LABEL_ACCENTS.map.color }}
              >
                <LabelIcon accent={LABEL_ACCENTS.map} size={12} />
                فتح الخريطة
              </a>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-white/5 pt-3">
          {clinic.service_group_id ? (
            <Button variant="subtle" icon="unlink" onClick={onUnlink}>
              فك ارتباط الخدمات
            </Button>
          ) : (
            <Button variant="subtle" icon="link" onClick={onLink}>
              ربط الخدمات
            </Button>
          )}
          <Button variant="ghost" icon="pencil" onClick={onEdit}>
            تعديل
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/* ========================================================================== */
/**
 * Add a clinic without leaving the page.
 *
 * Only the fields a clinic cannot open without. Everything else has a working
 * default, and a form that asks for deposit percentages before it has a name
 * is a form people abandon. "المزيد من الخيارات" hands the half-filled draft
 * to the full editor rather than starting it over.
 */
function InlineAddClinic({ onCancel, onSave, onNeedFullEditor }) {
  const [form, setForm] = useState(emptyClinic);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const update = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggleDay = (day) =>
    setForm((current) => {
      const days = current.workingDays || [];
      const has = days.some((d) => String(d).toLowerCase() === day);
      return {
        ...current,
        workingDays: has ? days.filter((d) => String(d).toLowerCase() !== day) : [...days, day],
      };
    });

  const preview = useMemo(() => {
    if (!form.startTime || !form.endTime || !form.duration) return [];
    return generateSlots(form.startTime, form.endTime, Number(form.duration), form.breaks || []);
  }, [form.startTime, form.endTime, form.duration, form.breaks]);

  const submit = async () => {
    const validation = validateClinic(form);
    setErrors(validation.errors);
    if (!validation.valid) return;

    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel className="space-y-5 border border-cyan-500/30 bg-cyan-500/[0.04]">
      <div className="flex items-center gap-2">
        <span className="rounded-lg bg-cyan-500/15 p-2 text-cyan-300">
          <Icon name="plus" size={16} />
        </span>
        <h3 className="text-base font-bold text-white">عيادة جديدة</h3>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="اسم العيادة" error={errors.name} required>
          <Input
            value={form.name}
            onChange={update('name')}
            placeholder="العيادة الرئيسية"
            autoFocus
          />
        </Field>
        <Field label="العنوان">
          <Input value={form.address || ''} onChange={update('address')} />
        </Field>
      </div>

      <Field label="أيام العمل" error={errors.workingDays} required>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {DAY_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setForm((current) => ({ ...current, workingDays: preset.days }))}
                className="rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {DAY_KEYS.map((day) => {
              const active = (form.workingDays || []).some(
                (d) => String(d).toLowerCase() === day,
              );
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                    active
                      ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                      : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {ARABIC_DAYS[day]}
                </button>
              );
            })}
          </div>
        </div>
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="بداية العمل" error={errors.startTime} required>
          <Input type="time" dir="ltr" value={form.startTime} onChange={update('startTime')} />
        </Field>
        <Field label="نهاية العمل" error={errors.endTime} required>
          <Input type="time" dir="ltr" value={form.endTime} onChange={update('endTime')} />
        </Field>
        <Field label="مدة الكشف (دقيقة)" error={errors.duration} required>
          <Input type="number" min="5" step="5" value={form.duration} onChange={update('duration')} />
        </Field>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {DURATION_PRESETS.map((minutes) => (
          <button
            key={minutes}
            type="button"
            onClick={() => setForm((current) => ({ ...current, duration: minutes }))}
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition-colors ${
              Number(form.duration) === minutes
                ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
            }`}
          >
            {minutes} د
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="سعر الكشف" error={errors.examination_price}>
          <Input
            type="number"
            min="0"
            value={form.examination_price}
            onChange={update('examination_price')}
          />
        </Field>
        <Field label="سعر المتابعة" error={errors.followup_price}>
          <Input
            type="number"
            min="0"
            value={form.followup_price}
            onChange={update('followup_price')}
          />
        </Field>
      </div>

      <Field label="لون العيادة" hint="يميّز العيادة في التقويم — لا يمكن تكراره">
        <ColorPicker value={form.color} onChange={(color) => setForm((c) => ({ ...c, color }))} />
      </Field>

      {preview.length > 0 && (
        <p className="text-[11px] font-bold text-emerald-300">
          {preview.length} موعد يومياً — من {formatTime12(preview[0])} إلى{' '}
          {formatTime12(preview[preview.length - 1])}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2 border-t border-white/10 pt-4">
        <Button variant="ghost" onClick={onCancel}>
          إلغاء
        </Button>
        <Button variant="subtle" icon="settings" onClick={() => onNeedFullEditor(form)}>
          المزيد من الخيارات
        </Button>
        <Button icon="save" loading={saving} onClick={submit}>
          حفظ العيادة
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

/**
 * The palette, plus a free colour.
 *
 * The eight-swatch grid was a hard ceiling: the backend refuses two clinics the
 * same colour per doctor, so a ninth clinic had no colour left to pick.
 */
function ColorPicker({ value, onChange }) {
  const custom = value && !CLINIC_COLORS.includes(value);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {CLINIC_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            className={`h-8 w-8 rounded-lg border-2 transition-transform ${
              value === color ? 'scale-110 border-white' : 'border-transparent'
            }`}
            style={{ backgroundColor: color }}
            aria-label={`اللون ${color}`}
          />
        ))}
      </div>

      <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1.5 text-[11px] font-bold text-slate-300 hover:border-cyan-500/50">
        <Icon name="pipette" size={13} className={custom ? 'text-cyan-300' : 'text-slate-500'} />
        {custom ? `لون مخصص (${value})` : 'لون مخصص'}
        <input
          type="color"
          value={value || '#06b6d4'}
          onChange={(event) => onChange(event.target.value)}
          className="h-5 w-8 cursor-pointer border-0 bg-transparent p-0"
        />
      </label>
    </div>
  );
}

/* ========================================================================== */

/**
 * A label glyph that glows in its own colour and breathes on its own beat.
 *
 * The halo is a drop-shadow of `currentColor`, so setting the colour is enough
 * — see .clinic-card__ico. Delay comes from the accent so the icons on one card
 * never pulse in unison.
 */
function LabelIcon({ accent, size = 11, className = '' }) {
  if (!accent) return null;
  return (
    <Icon
      name={accent.icon}
      size={size}
      className={`clinic-card__ico shrink-0 ${className}`}
      style={{ color: accent.color, animationDelay: accent.delay }}
    />
  );
}

function Cell({ accent, label, value }) {
  return (
    <div className="clinic-card__cell rounded-xl p-2.5">
      <dt className="flex items-center gap-1.5 text-[10px] text-slate-400">
        <LabelIcon accent={accent} />
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-xs font-bold text-slate-100">{value}</dd>
    </div>
  );
}

/* ========================================================================== */

function ClinicFormModal({ clinic, onClose, onSave }) {
  const [form, setForm] = useState(clinic);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(clinic);
    setErrors({});
  }, [clinic]);

  const preview = useMemo(() => {
    if (!form?.startTime || !form?.endTime || !form?.duration) return [];
    return generateSlots(form.startTime, form.endTime, Number(form.duration), form.breaks || []);
  }, [form?.startTime, form?.endTime, form?.duration, form?.breaks]);

  if (!form) return null;

  const update = (field) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggleDay = (day) =>
    setForm((current) => {
      const days = current.workingDays || [];
      const has = days.some((d) => String(d).toLowerCase() === day);
      return {
        ...current,
        workingDays: has ? days.filter((d) => String(d).toLowerCase() !== day) : [...days, day],
      };
    });

  const updateBreak = (index, field, value) =>
    setForm((current) => {
      const breaks = [...(current.breaks || [])];
      breaks[index] = { ...breaks[index], [field]: value };
      return { ...current, breaks };
    });

  const updateService = (index, field, value) =>
    setForm((current) => {
      const services = [...(current.extra_services || [])];
      services[index] = { ...services[index], [field]: value };
      return { ...current, extra_services: services };
    });

  const handleSubmit = () => {
    const validation = validateClinic(form);
    setErrors(validation.errors);
    if (!validation.valid) return;
    onSave(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل العيادة' : 'إضافة عيادة'}
      icon="building-2"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" onClick={handleSubmit}>
            حفظ العيادة
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Basics */}
        <section className="space-y-4">
          <h4 className="text-sm font-bold text-cyan-300">البيانات الأساسية</h4>

          <Field label="اسم العيادة" error={errors.name} required>
            <Input value={form.name} onChange={update('name')} />
          </Field>

          <Field label="العنوان">
            <Input value={form.address || ''} onChange={update('address')} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="الموبايل">
              <Input value={form.mobile || ''} onChange={update('mobile')} dir="ltr" />
            </Field>
            <Field label="الأرضي">
              <Input value={form.landline || ''} onChange={update('landline')} dir="ltr" />
            </Field>
          </div>

          <Field label="رابط الخريطة" hint="رابط Google Maps للعيادة">
            <Input value={form.mapUrl || ''} onChange={update('mapUrl')} dir="ltr" />
          </Field>

          <Field label="لون العيادة" hint="يميّز العيادة في التقويم — لا يمكن تكراره">
            <ColorPicker
              value={form.color}
              onChange={(color) => setForm((current) => ({ ...current, color }))}
            />
          </Field>
        </section>

        {/* Schedule */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">أيام وساعات العمل</h4>

          <Field label="أيام العمل" error={errors.workingDays} required>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {DAY_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, workingDays: preset.days }))}
                  className="rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {DAY_KEYS.map((day) => {
                const active = (form.workingDays || []).some(
                  (d) => String(d).toLowerCase() === day,
                );
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                      active
                        ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                        : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {ARABIC_DAYS[day]}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="بداية العمل" error={errors.startTime} required>
              <Input type="time" value={form.startTime || ''} onChange={update('startTime')} dir="ltr" />
            </Field>
            <Field label="نهاية العمل" error={errors.endTime} required>
              <Input type="time" value={form.endTime || ''} onChange={update('endTime')} dir="ltr" />
            </Field>
            <Field label="مدة الكشف (دقيقة)" error={errors.duration} required>
              <Input type="number" min="5" step="5" value={form.duration || ''} onChange={update('duration')} />
            </Field>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => setForm((current) => ({ ...current, duration: minutes }))}
                className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                  Number(form.duration) === minutes
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                    : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                }`}
              >
                {minutes} د
              </button>
            ))}
          </div>

          <Field label="عدد الحجوزات لكل موعد" hint="أكثر من واحد يعني استقبال أكثر من مريض في نفس التوقيت">
            <Input
              type="number"
              min="1"
              value={form.maxBookingsPerSlot || 1}
              onChange={update('maxBookingsPerSlot')}
              className="sm:w-40"
            />
          </Field>

          {/* Breaks */}
          <Field label="فترات الراحة">
            <div className="space-y-2">
              {(form.breaks || []).map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="time"
                    dir="ltr"
                    value={entry.start || ''}
                    onChange={(event) => updateBreak(index, 'start', event.target.value)}
                  />
                  <span className="text-xs text-slate-500">إلى</span>
                  <Input
                    type="time"
                    dir="ltr"
                    value={entry.end || ''}
                    onChange={(event) => updateBreak(index, 'end', event.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        breaks: current.breaks.filter((_, i) => i !== index),
                      }))
                    }
                    className="shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                    aria-label="حذف فترة الراحة"
                  >
                    <Icon name="trash-2" size={15} />
                  </button>
                </div>
              ))}

              <Button
                variant="subtle"
                icon="plus"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    breaks: [...(current.breaks || []), { start: '13:00', end: '14:00' }],
                  }))
                }
              >
                إضافة فترة راحة
              </Button>
            </div>
          </Field>

          {preview.length > 0 && (
            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
              <p className="mb-2 text-[11px] font-bold text-slate-300">
                معاينة المواعيد ({preview.length} موعد)
              </p>
              <div className="custom-scrollbar flex max-h-24 flex-wrap gap-1 overflow-y-auto">
                {preview.map((time) => (
                  <span
                    key={time}
                    className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300"
                  >
                    {time}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Pricing */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">الأسعار والعربون</h4>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="سعر الكشف" error={errors.examination_price}>
              <Input
                type="number"
                min="0"
                value={form.examination_price ?? ''}
                onChange={update('examination_price')}
              />
            </Field>
            <Field label="سعر المتابعة" error={errors.followup_price}>
              <Input
                type="number"
                min="0"
                value={form.followup_price ?? ''}
                onChange={update('followup_price')}
              />
            </Field>
          </div>

          <Checkbox
            label="طلب عربون لتأكيد الحجز"
            checked={Boolean(form.requireDeposit)}
            onChange={update('requireDeposit')}
          />

          {form.requireDeposit && (
            <div className="space-y-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="نسبة عربون الكشف (%)" error={errors.examination_deposit_percentage}>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.examination_deposit_percentage ?? ''}
                    onChange={update('examination_deposit_percentage')}
                  />
                </Field>
                <Field label="مبلغ ثابت للكشف" hint="اتركه فارغاً لاستخدام النسبة">
                  <Input
                    type="number"
                    min="0"
                    value={form.examination_deposit_amount ?? ''}
                    onChange={update('examination_deposit_amount')}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="نسبة عربون المتابعة (%)" error={errors.followup_deposit_percentage}>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={form.followup_deposit_percentage ?? ''}
                    onChange={update('followup_deposit_percentage')}
                  />
                </Field>
                <Field label="مبلغ ثابت للمتابعة" hint="اتركه فارغاً لاستخدام النسبة">
                  <Input
                    type="number"
                    min="0"
                    value={form.followup_deposit_amount ?? ''}
                    onChange={update('followup_deposit_amount')}
                  />
                </Field>
              </div>

              <Field label="حساب InstaPay" required hint="يظهر للمريض عند طلب تحويل العربون">
                <Input
                  value={form.instapay_account || ''}
                  onChange={update('instapay_account')}
                  dir="ltr"
                />
              </Field>
            </div>
          )}
        </section>

        {/* Extra services */}
        <section className="space-y-4 border-t border-white/10 pt-5">
          <h4 className="text-sm font-bold text-cyan-300">خدمات إضافية</h4>

          <div className="space-y-2">
            {(form.extra_services || []).map((service, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={service.service_name_ar || ''}
                  onChange={(event) => updateService(index, 'service_name_ar', event.target.value)}
                  placeholder="اسم الخدمة"
                  className="flex-1"
                />
                <Input
                  type="number"
                  min="0"
                  value={service.service_price ?? ''}
                  onChange={(event) => updateService(index, 'service_price', event.target.value)}
                  placeholder="السعر"
                  className="w-28"
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      extra_services: current.extra_services.filter((_, i) => i !== index),
                    }))
                  }
                  className="shrink-0 rounded-lg bg-red-500/10 p-2 text-red-300 hover:bg-red-500/20"
                  aria-label="حذف الخدمة"
                >
                  <Icon name="trash-2" size={15} />
                </button>
              </div>
            ))}

            <Button
              variant="subtle"
              icon="plus"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  extra_services: [
                    ...(current.extra_services || []),
                    { service_name_ar: '', service_price: 0 },
                  ],
                }))
              }
            >
              إضافة خدمة
            </Button>
          </div>

          {form.service_group_id && (
            <p className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-2.5 text-[11px] leading-relaxed text-purple-200">
              <Icon name="link" size={12} className="ml-1 inline" />
              خدمات هذه العيادة مرتبطة بعيادات أخرى — أي تعديل هنا سينعكس عليها جميعاً.
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
}

/* ========================================================================== */

function LinkServicesModal({ clinic, clinics, onClose, onLink }) {
  const [sourceId, setSourceId] = useState('');

  useEffect(() => setSourceId(''), [clinic]);

  if (!clinic) return null;

  const options = clinics.filter((c) => String(c.id) !== String(clinic.id));

  return (
    <Modal
      open
      onClose={onClose}
      title="ربط قائمة الخدمات"
      icon="link"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="link" disabled={!sourceId} onClick={() => onLink(sourceId)}>
            ربط
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-slate-300">
          سيتم نسخ قائمة الخدمات من العيادة المصدر إلى{' '}
          <strong className="text-white">{clinic.name}</strong>، وسيبقى الاثنان متزامنين بعد ذلك.
        </p>

        {options.length === 0 ? (
          <EmptyState icon="building" title="لا توجد عيادات أخرى للربط" />
        ) : (
          <Field label="العيادة المصدر">
            <Select value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
              <option value="">اختر العيادة</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name} ({option.extra_services?.length || 0} خدمة)
                </option>
              ))}
            </Select>
          </Field>
        )}

        <p className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200">
          تنبيه: سيتم استبدال قائمة الخدمات الحالية في {clinic.name} بالكامل.
        </p>
      </div>
    </Modal>
  );
}

export default DoctorClinics;
